/**
 * Onthoudt per bestands-URL het laatste oordeel van de bestandscheck, zodat
 * de winkelwagen het stoplicht kan tonen zonder het bestand opnieuw te laten
 * downloaden, en het `add_to_cart`-event de uitkomst kan meesturen.
 *
 * Alleen gemak: localStorage kan ontbreken of weigeren (privévenster), dan
 * blijft het bij het geheugen van deze pagina. Er staat geen bestandsnaam in,
 * alleen het niveau, de dpi en de codes.
 */
import type { FileCheckLevel } from './print-file-check';

export type FileCheckSummary = { level: FileCheckLevel; dpi: number | null; codes: string[]; at: number };

const STORAGE_KEY = 'printen_file_checks_v1';
const MAX_ENTRIES = 40;
const memory = new Map<string, FileCheckSummary>();
let loaded = false;

function load() {
  if (loaded) return;
  loaded = true;
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    const parsed = raw ? JSON.parse(raw) : null;
    if (parsed && typeof parsed === 'object') {
      for (const [url, value] of Object.entries(parsed as Record<string, FileCheckSummary>)) {
        if (value && typeof value.level === 'string') memory.set(url, value);
      }
    }
  } catch {
    // Geen opslag: alleen het geheugen van deze pagina.
  }
}

function persist() {
  try {
    const entries = Array.from(memory.entries()).sort((a, b) => b[1].at - a[1].at).slice(0, MAX_ENTRIES);
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(Object.fromEntries(entries)));
  } catch {
    // Vol of geblokkeerd: niet erg, het is alleen een weergavehulp.
  }
}

/** Sleutel zonder querystring: een Blob-URL krijgt soms een cachebuster. */
function keyFor(url: string) {
  return String(url || '').split('?')[0];
}

export function rememberFileCheck(url: string | null | undefined, summary: Omit<FileCheckSummary, 'at'>) {
  if (!url || typeof window === 'undefined') return;
  load();
  memory.set(keyFor(url), { ...summary, at: Date.now() });
  persist();
}

export function readFileCheck(url: string | null | undefined): FileCheckSummary | null {
  if (!url || typeof window === 'undefined') return null;
  load();
  return memory.get(keyFor(url)) || null;
}

/** Het zwakste stoplicht over meerdere bestanden van een regel; null als er niets bekend is. */
export function worstFileCheck(urls: Array<string | null | undefined>): FileCheckSummary | null {
  const order: Record<FileCheckLevel, number> = { red: 3, orange: 2, unchecked: 1, green: 0 };
  let worst: FileCheckSummary | null = null;
  for (const url of urls) {
    const summary = readFileCheck(url);
    if (summary && (!worst || order[summary.level] > order[worst.level])) worst = summary;
  }
  return worst;
}

/** Korte, stabiele hash voor een eventId (de tracker eist [A-Za-z0-9_.:-]). */
export function stableEventHash(input: string) {
  let hash = 2166136261;
  for (let index = 0; index < input.length; index++) {
    hash ^= input.charCodeAt(index);
    hash = Math.imul(hash, 16777619);
  }
  return (hash >>> 0).toString(36);
}

const trackedResults = new Set<string>();

/**
 * Een `file_check_result` per bestand, formaat en uitkomst. Effecten draaien
 * vaker dan de uitkomst verandert; zonder deze ontdubbeling telt GA4 elke
 * herberekening als nieuwe check.
 */
export function trackFileCheckResult(input: { url: string; sizeKey: string; level: FileCheckLevel; codes: string[]; productSlug?: string; source: 'upload' | 'editor' }) {
  if (typeof window === 'undefined') return;
  const key = `${keyFor(input.url)}|${input.sizeKey}|${input.level}`;
  if (trackedResults.has(key)) return;
  trackedResults.add(key);
  try {
    window.printenTrack?.('file_check_result', {
      eventId: `fcr_${stableEventHash(key)}`,
      file_check_status: input.level,
      file_check_issues: input.codes.join(',') || 'none',
      file_check_source: input.source,
      product_slug: input.productSlug || undefined,
    });
  } catch {
    // Meten mag de check nooit hinderen.
  }
}

export function trackFileCheckFix(fix: 'smaller_size' | 'editor' | 'upscale' | 'dpi_checker' | 'cart_edit', input: { level: FileCheckLevel; productSlug?: string }) {
  if (typeof window === 'undefined') return;
  try {
    window.printenTrack?.('file_check_fix_click', {
      file_check_fix: fix,
      file_check_status: input.level,
      product_slug: input.productSlug || undefined,
    });
  } catch {
    // Meten mag een klik nooit tegenhouden.
  }
}
